import React, { FC, useState } from 'react';
import ContractModal from '@hub/components/contract-modal/contract-modal';
import { PaymentRequiredAlert } from '@hub/features/dashboard-templates/dashboard-common/payment-required-alert/payment-required-alert';
import { PaymentRequiredModal } from '@hub/features/dashboard-templates/dashboard-common/payment-required-modal/payment-required-modal';
import { WithdrawalProcessingBanner } from '@hub/features/dashboard-templates/dashboard-common-new/withdrawal-processing-banner';
import { useStore } from '@hub/helpers/use-store';
import { EnvService } from '@hub/services/core/env-service';
import { useBooleanFeatureFlag } from '@monorepo/feature-flag/client';
import { FLAG } from '@monorepo/feature-flag/shared';
import { AccountStatus, AccountType, ContractType } from '@monorepo/types';
import { useDashboard } from '../../contexts';
import { DashboardErrorView } from '../dashboard-error-view';
import {
  AccountStatistics,
  ActivityLog,
  DashboardHeader,
  DashboardLayout,
  DashboardLoading,
  KeyMetrics,
  PerformanceChart,
  ProgressOverview,
  PromotionalBanners,
  ThankYouBanner,
} from '../index';
import { TerminationReasonModal } from '../termination-reason-modal';

export const DashboardContent: FC = () => {
  const { account, isLoading, error, retry } = useDashboard();
  const [isShowPaymentRequiredModal, setIsShowPaymentRequiredModal] =
    useState(false);
  const {
    dataStore: { userStore },
  } = useStore();
  const isTerminationPopupEnabled = useBooleanFeatureFlag(
    FLAG.SHOW_DASHBOARD_TERMINATION_POPUP,
    { env: EnvService.application },
  );
  const showTerminationPopup =
    isTerminationPopupEnabled && !userStore.isImpersonated;

  if (isLoading) {
    return <DashboardLoading />;
  }

  if (error || !account) {
    return <DashboardErrorView error={error} onRetry={retry} />;
  }

  const showWithdrawalBanner =
    account.status === AccountStatus.WaitingForWithdraw;

  const isFundedAccount = account?.type === AccountType.Funded;

  return (
    <DashboardLayout>
      {showTerminationPopup && <TerminationReasonModal />}
      {isFundedAccount && <ContractModal contractType={ContractType.Funded} />}
      {isFundedAccount && (
        <ContractModal contractType={ContractType.FundedRules} />
      )}
      <ContractModal contractType={ContractType.Nyse} />
      <ThankYouBanner />
      {showWithdrawalBanner && <WithdrawalProcessingBanner />}
      <PaymentRequiredModal
        isOpen={isShowPaymentRequiredModal}
        onClose={() => setIsShowPaymentRequiredModal(false)}
        accountId={account._id}
        amount={account.additionalPayment || 0}
      />
      {Boolean(account.additionalPayment) && (
        <PaymentRequiredAlert
          amount={account.additionalPayment || 0}
          onCompleteClick={() => setIsShowPaymentRequiredModal(true)}
        />
      )}
      <ProgressOverview />
      <DashboardHeader />
      <PerformanceChart />
      <KeyMetrics />
      <ActivityLog />
      <AccountStatistics />
      <PromotionalBanners />
    </DashboardLayout>
  );
};
